import { FC, useEffect, useState } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FieldError, FieldValues, FormProvider, Resolver, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import {
	useWriteSurveyAnswersMutation,
	useLazyGetEngagementInformationBySurveyQuery,
	useLazyGetSurveyByQrCodeIdQuery,
} from '../../../../store/api/survey.api';
import { SurveyQuestionRender } from '../../components/question/question.component';
import { SurveyHeader } from '../../../_core/components/header-survey/header-survey.component';
import { CoreButton, CoreLinkButton } from '../../../_core/components/_ui/core-button';
import { SurveyQuestionInterface, SurveyQuestionTypeEnum } from '@families-link/shared';
import { CoreTextField } from '../../../_core/components/_ui/core-textfield';
import { PhoneInput } from '../../../_core/components/_ui/phone-input';
import { useMuiModal } from '../../../_core/hooks';
import { CoreModal, CoreModalProps } from '../../../_core/components/_ui/core-modal';
import { useSnackbar } from 'notistack';

const selectLanguageObject: { [key: string]: string } = {
	Arabic: 'ar',
	ar: 'ar',
	Spanish: 'es',
	es: 'es',
};

const warningModalComponent: FC<CoreModalProps & { phoneNumber: string }> = (props) => (
	<CoreModal
		{...props}
		modalWidth={500}
		headerComponent={
			<Typography
				sx={{ textAlign: 'center', fontWeight: 'normal' }}
			>{`Answer for phone number ${props.phoneNumber} is already existed!`}</Typography>
		}
	></CoreModal>
);

export const SurveyShareViewPage: FC = () => {
	const { t, i18n } = useTranslation('survey');
	const [isSuccessComponent, setIsSuccessComponent] = useState(false);
	const [triggerGetSurveyByQrCodeIdQuery, { data: survey, isFetching }] = useLazyGetSurveyByQrCodeIdQuery();
	const [triggerGetEngagementInformationBySurveyQuery, { data: engagementInformation }] =
		useLazyGetEngagementInformationBySurveyQuery();
	const [writeSurveyAnswers, { isError: isWriteSurveyAnswerError, error: writeSurveyAnswerError, isSuccess }] =
		useWriteSurveyAnswersMutation();

	useEffect(() => {
		if (isSuccess && !isWriteSurveyAnswerError) {
			setIsSuccessComponent(true);
		}
		if (isWriteSurveyAnswerError) {
			if ((writeSurveyAnswerError as any)?.data?.statusCode === 409) {
				openWarningModal({
					phoneNumber: phoneNumberInForm,
				});
				setError('phoneNumber', { type: 'custom', message: 'This phone number is already taken.' });
			} else {
				showToast('Something goes wrong', 'error');
			}
		}
	}, [isWriteSurveyAnswerError, isSuccess]);

	const { qrId } = useParams();

	useEffect(() => {
		if (qrId) {
			triggerGetSurveyByQrCodeIdQuery({ qrCodeId: qrId });
		}
	}, [qrId, triggerGetSurveyByQrCodeIdQuery]);

	const surveyId = survey?._id;
	useEffect(() => {
		if (surveyId) {
			triggerGetEngagementInformationBySurveyQuery(surveyId);
		}
	}, [surveyId, triggerGetEngagementInformationBySurveyQuery]);

	const browserLanguage = navigator.language;

	const templateLanguageArabicOrSpanishArray = ['Arabic', 'Spanish'];
	const browserLanguageArabicOrSpanishArray = ['ar', 'es'];
	const surveyTemplate = survey?.surveyTemplate;
	const surveyTranslate = survey?.surveyTemplate?.translates?.find((translate)=>translate.language === survey.engagementLanguage);

	const selectedLanguage =
		surveyTranslate?.language &&
		templateLanguageArabicOrSpanishArray.includes(surveyTranslate.language)
			? selectLanguageObject[surveyTranslate.language]
			: browserLanguageArabicOrSpanishArray.includes(browserLanguage)
			? selectLanguageObject[browserLanguage]
			: 'en';

	useEffect(() => {
		i18n.changeLanguage(selectedLanguage);
	}, [i18n, surveyTranslate?.language, browserLanguage]);

	const defaultValues = {
		familyId: null,
		surveyId,
		fullName: '',
		phoneNumber: '',
		...surveyTemplate?.questions?.reduce((result: any, el: SurveyQuestionInterface) => {
			if (el.type === SurveyQuestionTypeEnum.Options || el.type === SurveyQuestionTypeEnum.MultiSelect) {
				result[`option_field_${el._id}`] = '';
				result[`additional_option_field_${el._id}`] = '';
				return result;
			} else {
				result[`field_${el._id}`] = '';
				return result;
			}
		}, {}),
	};

	const customResolver: Resolver = async (values: FieldValues, context: { surveyTemplate: any }) => {
		const errors: { [key: string]: FieldError } = {};

		if (context.surveyTemplate) {
			context.surveyTemplate.questions.forEach((question: SurveyQuestionInterface) => {
				switch (question.type) {
					case SurveyQuestionTypeEnum.Options: {
						const fieldName = `option_field_${question._id}`;
						const additionalFieldName = `additional_option_field_${question._id}`;
						const fieldValue = values[fieldName];
						const additionalFieldValue = values[additionalFieldName];
						if (!fieldValue) {
							if (question.isRequired) {
								errors[fieldName] = {
									type: 'required',
									message: t('survey.forms.errors.required-field'),
								};
							}
						} else if (JSON.parse(fieldValue)?.isOtherOption && !additionalFieldValue) {
							errors[additionalFieldName] = {
								type: 'required',
								message: 'You selected an option that needs more information. Please fill the field.',
							};
						}

						break;
					}
					case SurveyQuestionTypeEnum.MultiSelect: {
						const fieldName = `option_field_${question._id}`;
						const additionalFieldName = `additional_option_field_${question._id}`;
						const fieldValue = values[fieldName];
						const additionalFieldValue = values[additionalFieldName];
						if (Array.isArray(fieldValue) && fieldValue.length === 0) {
							if (question.isRequired) {
								errors[fieldName] = {
									type: 'required',
									message: t('survey.forms.errors.required-field'),
								};
							}
						} else if (
							Array.isArray(fieldValue) &&
							fieldValue.find((value: string) => JSON.parse(value)?.isOtherOption) &&
							!additionalFieldValue
						) {
							errors[additionalFieldName] = {
								type: 'required',
								message: 'You selected an option that needs more information. Please fill the field.',
							};
						}

						break;
					}
					default: {
						const fieldName = `field_${question._id}`;
						const fieldValue = values[fieldName];

						if (question.isRequired) {
							if (!fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0)) {
								errors[fieldName] = {
									type: 'required',
									message: t('survey.forms.errors.required-field'),
								};
							}
						}

						break;
					}
				}
			});
		}

		if (!values['fullName']) {
			errors['fullName'] = {
				type: 'required',
				message: t('survey.forms.errors.full-name'),
			};
		}

		if (!values['phoneNumber']) {
			errors['phoneNumber'] = {
				type: 'required',
				message: t('survey.forms.errors.phone-number'),
			};
		}

		return {
			values,
			errors,
		};
	};

	const methods = useForm({
		defaultValues,
		resolver: async (values, fieldNames) => customResolver(values, { surveyTemplate }, fieldNames),
		mode: 'all',
	});

	const {
		register,
		control,
		handleSubmit,
		setError,
		watch,
		formState: { isDirty, isLoading, isSubmitting, isSubmitted, isValid, errors },
	} = methods;

	const disableSubmit = !isValid && (isDirty || isSubmitted);
	const loadingSubmit = isLoading || isSubmitting;

	const phoneNumberInForm = watch('phoneNumber');

	const { openModal: openWarningModal } = useMuiModal(warningModalComponent);
	const { enqueueSnackbar } = useSnackbar();

	const showToast = (message: string, variant: 'error' | 'default' | 'success' | 'warning' | 'info' | undefined) => {
		enqueueSnackbar(message, {
			variant,
		});
	};

	const onSubmit = async (values: any) => {
		const transformValues = Object.keys(values).reduce(
			(submitObject: any, key: string) => {
				if (key.startsWith('field_')) {
					const fieldId = key.replace('field_', '');
					submitObject['answers'].push({
						fieldId,
						value: { text: values[key] },
					});
				} else if (key.startsWith('option_field_')) {
					const fieldId = key.replace('option_field_', '');
					submitObject['answers'].push({
						fieldId,
						value: !values[key]
							? values[key] : Array.isArray(values[key])
							? values[key].map((el: any) => ({
									text: JSON.parse(el)?.translatedText,
									optionId: JSON.parse(el)?._id,
									additionalText: JSON.parse(el)?.isOtherOption
										? values[`additional_option_field_${fieldId}`]
										: undefined,
							  }))
							: {
									text: JSON.parse(values[key])?.translatedText,
									optionId: JSON.parse(values[key])?._id,
									additionalText: JSON.parse(values[key])?.isOtherOption
										? values[`additional_option_field_${fieldId}`]
										: undefined,
							  },
					});
				} else if (key.startsWith('additional_option_field_')) {
					//skip
				} else {
					submitObject[key] = values[key];
				}
				submitObject['surveyId'] = surveyId;
				submitObject['language'] = surveyTranslate?.language;
				return submitObject;
			},
			{ answers: [] }
		);
		await writeSurveyAnswers(transformValues);
	};

	return (
		<Box
			sx={{
				display: 'flex',
				flex: 1,
				justifyContent: 'center',
				minHeight: 0,
				height: '100%',
				direction: selectedLanguage === 'ar' ? 'rtl' : 'ltr',
				textAlign: selectedLanguage === 'ar' ? 'right' : 'left',
			}}
		>
			{isSuccessComponent ? (
				<Box
					sx={{
						width: '100%',
						height: '100vh',
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						alignItems: 'center',
						gap: 2,
					}}
				>
					<Typography variant="h1" sx={{ fontSize: '32px' }}>
						{t('survey.completed-survey')}
					</Typography>
					<CoreLinkButton onClick={() => setIsSuccessComponent(false)} sx={{ fontSize: '16px' }}>
						{t('survey.return-to-survey')}
					</CoreLinkButton>
				</Box>
			) : !isFetching && (!surveyTemplate || !surveyId) ? (
				<Box sx={{ width: '100%', height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
					<Typography>{t('survey-templates.deleted-survey-template')}</Typography>
				</Box>
			) : !isFetching && !survey?.qrCode ? (
				<Box sx={{ width: '100%', height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
					<Typography>{t('survey.expired-link')}</Typography>
				</Box>
			) : surveyTemplate ? (
				<Box sx={{ width: '100%', height: '100%' }}>
					<Box component="header" sx={{ mb: 1 }}>
						<SurveyHeader
							title={surveyTemplate?.title}
							engagementType={engagementInformation?.type}
							engagementDuration={engagementInformation?.duration}
							engagementSchool={engagementInformation?.school}
						/>
					</Box>

					<FormProvider {...methods}>
						<Box
							component="form"
							onSubmit={handleSubmit(onSubmit)}
							sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
						>
							<Paper elevation={0} sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
								<CoreTextField
									label={t('survey.forms.labels.full-name')}
									requiredMark
									fullWidth
									placeholder={t('survey.forms.placeholders.full-name') ?? ''}
									{...register('fullName')}
									error={!!errors['fullName']}
									helperText={(errors['fullName'] as FieldError)?.message}
									customLabelSx={{ fontSize: '14px', fontWeight: '500', color: 'black' }}
									controlSx={{ marginY: 1 }}
								/>
								<PhoneInput
									fullWidth
									id="phone-number"
									requiredMark
									label={t('survey.forms.labels.phone-number')}
									control={control}
									name="phoneNumber"
									error={!!errors['phoneNumber']}
									helperText={(errors['phoneNumber'] as FieldError)?.message}
									customLabelSx={{ fontSize: '14px', fontWeight: '500', color: 'black' }}
									controlSx={{ marginY: 1 }}
								/>
							</Paper>
							{surveyTemplate?.questions?.map((question: SurveyQuestionInterface, index: number) => {
								const translatedQuestion = surveyTranslate?.isFinished ? surveyTranslate?.questions?.find(
									(translatedQuestion: any) => translatedQuestion.questionId === question._id
								): undefined;
								return (
									<Paper
										key={question._id.toString()}
										elevation={0}
										sx={{ p: 3, display: 'flex', flexDirection: 'column' }}
									>
										<SurveyQuestionRender
											question={question}
											translatedQuestion={translatedQuestion}
											customLabel={`${index + 1}. ${
												translatedQuestion ? translatedQuestion.translatedText : question.text
											}`}
										/>
									</Paper>
								);
							})}
							<Box>
								<CoreButton type="submit" contraModeStyleButton disabled={disableSubmit} loading={loadingSubmit}>
									{t('survey.forms.buttons.submit')}
								</CoreButton>
							</Box>
						</Box>
					</FormProvider>
				</Box>
			) : null}
		</Box>
	);
};
